import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Plane, ArrowRight } from "lucide-react";
import { BRAND, SEO_BASE } from "../config/brand";
import { useLang } from "../i18n/LanguageContext";
import { CITIES, buildPage, cityName } from "../data/seoData";

const CT = {
  de: { kicker: "Alle Destinationen · Schweiz", title: "Flughafentransfer Zürich in jede Gemeinde", intro: (n) => `Wir fahren Sie zum Fixpreis vom Flughafen Zürich (ZRH) in alle ${n} Gemeinden der Schweiz. Wählen Sie Ihren Kanton und Ihre Gemeinde für Distanz, Fixpreis und Buchung.`, index: "Nach Kanton", cities: "Gemeinden", back: "Zur Startseite", cta: "Preis berechnen" },
  en: { kicker: "All destinations · Switzerland", title: "Zurich Airport Transfer to every municipality", intro: (n) => `We drive you at a fixed price from Zurich Airport (ZRH) to all ${n} municipalities in Switzerland. Pick your canton and town for distance, fixed price and booking.`, index: "By canton", cities: "municipalities", back: "Back to home", cta: "Calculate price" },
  fr: { kicker: "Toutes les destinations · Suisse", title: "Transfert aéroport de Zurich vers chaque commune", intro: (n) => `Nous vous conduisons à prix fixe depuis l'aéroport de Zurich (ZRH) vers les ${n} communes de Suisse. Choisissez votre canton et votre commune pour la distance, le prix fixe et la réservation.`, index: "Par canton", cities: "communes", back: "Retour à l'accueil", cta: "Calculer le prix" },
  it: { kicker: "Tutte le destinazioni · Svizzera", title: "Transfer aeroporto di Zurigo verso ogni comune", intro: (n) => `Ti portiamo a prezzo fisso dall'aeroporto di Zurigo (ZRH) verso tutti i ${n} comuni della Svizzera. Scegli il tuo cantone e comune per distanza, prezzo fisso e prenotazione.`, index: "Per cantone", cities: "comuni", back: "Torna alla home", cta: "Calcola il prezzo" },
};

const kSlug = (k) => k.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const ZieleDirectory = () => {
  const { lang } = useLang();
  const L = lang && CT[lang] ? lang : "de";
  const t = CT[L];

  const groups = useMemo(() => {
    const by = {};
    CITIES.forEach((c) => { (by[c.k] = by[c.k] || []).push(c); });
    const cantons = Object.keys(by).sort((a, b) => {
      if (a === "Zürich") return -1;
      if (b === "Zürich") return 1;
      return a.localeCompare(b, "de");
    });
    cantons.forEach((k) => by[k].sort((a, b) => a.km - b.km));
    return { cantons, by };
  }, []);

  useEffect(() => {
    const url = `${SEO_BASE}/ziele-alle`;
    document.title = `${t.title} | ${BRAND.name}`;
    upsertMeta("name", "description", t.intro(CITIES.length).slice(0, 300));
    upsertCanonical(url);
    upsertMeta("property", "og:title", t.title);
    upsertMeta("property", "og:url", url);
    window.scrollTo(0, 0);
  }, [L, t]);

  return (
    <div className="bg-white" data-testid="ziele-directory-page">
      <section className="relative pt-28 pb-14 bg-[#0B1533] overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <p className="font-body text-xs uppercase tracking-[0.28em] text-[#C9A24B] mb-3">{t.kicker}</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">{t.title}</h1>
          <p className="font-body text-white/75 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">{t.intro(CITIES.length)}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="sticky top-[68px] z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-md border-b border-[#E9E5DE]">
          <p className="font-body text-xs uppercase tracking-wider text-[#5A607A] mb-2">{t.index}</p>
          <div className="flex flex-wrap gap-2" data-testid="canton-index">
            {groups.cantons.map((k) => (
              <a key={k} href={`#k-${kSlug(k)}`} data-testid={`canton-chip-${kSlug(k)}`} className="font-body text-xs bg-[#F5F2ED] text-[#0B1533] rounded-full px-3 py-1.5 hover:bg-[#2B4BFF] hover:text-white transition-colors">
                {k} <span className="opacity-60">{groups.by[k].length}</span>
              </a>
            ))}
          </div>
        </div>

        {groups.cantons.map((k) => (
          <section key={k} id={`k-${kSlug(k)}`} className="pt-10 scroll-mt-40" data-testid={`canton-section-${kSlug(k)}`}>
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-[#2B4BFF]" />
              <h2 className="font-heading font-bold text-xl text-[#0B1533]">{k}</h2>
              <span className="font-body text-sm text-[#9CA0B0]">· {groups.by[k].length} {t.cities}</span>
              <Link to={`/${L}/kanton/${kSlug(k)}`} data-testid={`canton-page-link-${kSlug(k)}`} className="ml-auto font-body text-xs text-[#2B4BFF] hover:underline shrink-0">Kanton {k} →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {groups.by[k].map((c) => {
                const pg = buildPage(L, "transfer", c);
                return (
                  <Link key={c.s} to={`/${L}/${pg.slug}`} data-testid={`city-link-${c.s}`} className="group flex items-center gap-2 rounded-xl border border-[#E9E5DE] bg-white px-3 py-2.5 hover:border-[#2B4BFF] hover:elev-soft transition-all">
                    <Plane size={14} className="text-[#2B4BFF] shrink-0" />
                    <span className="font-heading font-semibold text-sm text-[#0B1533] truncate flex-1">{cityName(c, L)}</span>
                    <span className="font-body text-xs text-[#9CA0B0] shrink-0">{c.km} km</span>
                    <ArrowRight size={14} className="text-[#2B4BFF] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        <div className="mt-12 text-center">
          <Link to={`/${L}`} className="font-body text-sm text-[#2B4BFF] hover:underline">← {t.back}</Link>
        </div>
      </div>
    </div>
  );
};

export default ZieleDirectory;
